/**
 * Fazenda — o cenario unico e persistente.
 *
 * Nao existem fases separadas nem troca de mundo (secao 1). Ha UM cenario, e
 * tudo que a crianca constroi fica nele para sempre.
 *
 * A fazenda e sempre reconstruida a partir do estado salvo: as construcoes que
 * ja existem aparecem coloridas; as que ainda nao foram feitas aparecem em
 * cinza dessaturado. O contraste e a recompensa visual (secao 12).
 */

import * as THREE from 'three';
import { criarAncora, POSICOES, posicoesDeBuracos } from './Ancoras.js';
import { clonar, clonarAnimal, clonarPessoa, prepararCache, TILE, ALTURA_SOLO } from './Modelos.js';
import { InstancedMesh, Object3D } from 'three';

// Ancoras que estao sempre no cenario, desde o primeiro dia.
const SEMPRE_PRESENTES = ['canteiro', 'cozinha', 'bancada', 'celeiro', 'caderno', 'ceu'];

// Ancoras que so aparecem quando a construcao correspondente e liberada.
const POR_CONSTRUCAO = [
  'cerca', 'galinheiro', 'poco', 'composteira', 'moinho',
  'estufa', 'roda_dagua', 'ponte',
];

export class Fazenda {
  constructor(sceneManager) {
    this.sm = sceneManager;
    this.raiz = new THREE.Group();
    this.raiz.name = 'fazenda';
    this.ancoras = new Map();
    this.arvores = [];
    this.plantas = new Map(); // id do canteiro -> grupo de plantas
    this.animais = [];
    this.pessoas = [];
    sceneManager.adicionar(this.raiz);
  }

  /**
   * Carrega todos os modelos .glb antes de montar o cenario.
   * Precisa rodar uma vez, antes de construir().
   */
  static async carregarModelos(aoProgredir) {
    return prepararCache(aoProgredir);
  }

  /** Monta o cenario inteiro a partir do estado. */
  construir(estado) {
    this.criarChao();
    this.criarArvores();
    this.criarVegetacao();

    for (const nome of SEMPRE_PRESENTES) {
      this.adicionarAncora(nome, false);
    }

    for (const nome of POR_CONSTRUCAO) {
      const construida = Boolean(estado.construcoes[nome]);
      this.adicionarAncora(nome, !construida);
    }

    this.criarAnimais();
    this.criarPessoas();
    this.restaurarCanteiros(estado);

    console.info(
      `FAZENDA: cenario montado — ${this.ancoras.size} ancoras, ` +
        `${Object.keys(estado.construcoes).length} construcao(oes) feita(s).`
    );
    return this;
  }

  criarChao() {
    // A ilha e compacta de proposito: um diorama que cabe todo na tela, em vez
    // de um campo enorme com as construcoes perdidas nele.
    const RAIO = 7; // em tiles, para cada lado -> 15x15
    const lado = RAIO * 2 + 1;
    const total = lado * lado;

    // InstancedMesh: os 225 blocos viram UMA chamada de desenho, e as 225
    // tampas de grama outra. Um objeto por tile daria 450 chamadas, e o
    // orcamento da secao 12 e para GPU integrada fraca.
    const blocos = this.instanciar('chao_terra', total);
    const grama = this.instanciar('grama', total);

    const molde = new Object3D();
    let n = 0;

    for (let i = -RAIO; i <= RAIO; i++) {
      for (let j = -RAIO; j <= RAIO; j++) {
        const x = i * TILE;
        const z = j * TILE;

        molde.position.set(x, 0, z);
        molde.updateMatrix();
        blocos.setMatrixAt(n, molde.matrix);

        // A grama sobe 2 centimetros acima do topo do bloco.
        //
        // Sem essa folga as duas superficies ficam EXATAMENTE no mesmo plano e
        // a placa de video nao sabe qual desenhar na frente. O resultado era um
        // xadrez verde e laranja: em cada tile ganhava uma delas, conforme o
        // arredondamento da posicao.
        molde.position.set(x, ALTURA_SOLO + 0.02, z);
        molde.updateMatrix();
        grama.setMatrixAt(n, molde.matrix);

        n += 1;
      }
    }

    blocos.instanceMatrix.needsUpdate = true;
    grama.instanceMatrix.needsUpdate = true;

    this.raiz.add(blocos, grama);
    this.chao = grama; // a grama e o que muda de cor com a estacao
    this.blocosDeTerra = blocos;
    this.raioDaIlha = RAIO * TILE;
  }

  /** Cria um InstancedMesh a partir da primeira malha de um modelo. */
  instanciar(papel, quantidade) {
    const modelo = clonar(papel);
    let malha = null;
    modelo.traverse((o) => {
      if (!malha && o.isMesh) malha = o;
    });

    if (!malha) {
      throw new Error(
        `FAZENDA: o modelo "${papel}" nao tem nenhuma malha para instanciar. ` +
          'Confira se o arquivo .glb esta correto.'
      );
    }

    const instancias = new InstancedMesh(malha.geometry, malha.material, quantidade);
    instancias.name = `chao-${papel}`;
    instancias.receiveShadow = true;
    return instancias;
  }

  /**
   * Arvores, pedras e detalhes espalhados.
   * Espalhados por sorteio deterministico: a fazenda e sempre a mesma a cada
   * abertura, porque o mundo e unico e permanente (secao 1).
   */
  criarVegetacao() {
    const enfeites = ['pedra_a', 'pedra_b', 'pedra_c', 'toco', 'maca', 'laranja'];

    let semente = 91;
    const sortear = () => {
      semente = (semente * 1103515245 + 12345) % 2147483648;
      return semente / 2147483648;
    };

    const alcance = (this.raioDaIlha - TILE) * 2;

    for (let i = 0; i < 26; i++) {
      const x = (sortear() - 0.5) * alcance;
      const z = (sortear() - 0.5) * alcance;

      if (this.pertoDeAlgumaAncora(x, z, 5)) continue;  // nao encosta nas ancoras

      const enfeite = clonar(enfeites[Math.floor(sortear() * enfeites.length)]);
      enfeite.position.set(x, ALTURA_SOLO, z);
      enfeite.rotation.y = sortear() * Math.PI * 2;
      this.raiz.add(enfeite);
    }
  }

  /**
   * Os animais da fazenda. D14 nao se aplica a eles: sao cenario vivo, nao
   * progresso. Ficam andando devagar em volta do proprio lugar.
   */
  criarAnimais() {
    const moradores = [
      ['galinha', 7.5, 5], ['galinha', 8.8, 3.6], ['galinha', 6.4, 4],
      ['cachorro', -2, 3], ['cavalo', 9, -5], ['gato', -7, -5],
    ];

    for (const [tipo, x, z] of moradores) {
      const bicho = clonarAnimal(tipo);
      bicho.position.set(x, ALTURA_SOLO, z);
      bicho.rotation.y = (x + z) % (Math.PI * 2);
      bicho.scale.setScalar(tipo === 'cavalo' ? 2.2 : 1.8);

      // Cada um anda um pouquinho em volta de onde nasceu.
      bicho.userData.casa = { x, z };
      bicho.userData.fase = (x * 13 + z * 7) % (Math.PI * 2);

      this.raiz.add(bicho);
      this.animais.push(bicho);
    }
  }

  /**
   * As pessoas da fazenda. Secao 12 pede personagens de cabeca e olhos grandes
   * — e o que este pacote entrega.
   */
  criarPessoas() {
    const moradores = [[0, -4.5, -6], [3, 5.5, -3]];

    for (const [indice, x, z] of moradores) {
      const pessoa = clonarPessoa(indice);
      pessoa.position.set(x, ALTURA_SOLO, z);
      pessoa.rotation.y = Math.PI * 0.85;
      pessoa.scale.setScalar(2.4);
      this.raiz.add(pessoa);
      this.pessoas.push(pessoa);
    }
  }

  pertoDeAlgumaAncora(x, z, distancia) {
    for (const p of Object.values(POSICOES)) {
      if (Math.hypot(x - p[0], z - p[2]) < distancia) return true;
    }
    return false;
  }

  criarArvores() {
    // Arvores na borda da ilha, emoldurando sem atrapalhar as tarefas.
    const borda = this.raioDaIlha - TILE;
    const posicoes = [
      [-borda, -borda, 'arvore'], [borda, -borda, 'pinheiro'],
      [-borda, borda, 'pinheiro_b'], [borda, borda, 'arvore'],
      [-borda, 0, 'pinheiro'], [borda, 2, 'arvore'],
      [-4, -borda, 'arvore'], [6, borda, 'pinheiro_b'],
    ];

    for (const [x, z, modelo] of posicoes) {
      const arvore = clonar(modelo);
      arvore.position.set(x, ALTURA_SOLO, z);
      arvore.rotation.y = (x * 7 + z * 3) % Math.PI;
      this.raiz.add(arvore);
      this.arvores.push(arvore);
    }
  }

  adicionarAncora(nome, adormecida) {
    const objeto = criarAncora(nome, adormecida);
    this.raiz.add(objeto);
    this.ancoras.set(nome, objeto);
    return objeto;
  }

  /** Acorda uma construcao: troca o cinza pela cor. */
  acordarAncora(nome) {
    const antiga = this.ancoras.get(nome);
    if (antiga) this.raiz.remove(antiga);

    const nova = this.adicionarAncora(nome, false);
    console.info(`FAZENDA: "${nome}" saiu do cinza. A fazenda cresceu.`);
    return nova;
  }

  ancora(nome) {
    const objeto = this.ancoras.get(nome);
    if (!objeto) {
      throw new Error(
        `FAZENDA: a ancora "${nome}" nao esta no cenario. ` +
          'Ou ela nao foi construida ainda, ou o nome esta errado no JSON da tarefa.'
      );
    }
    return objeto;
  }

  /**
   * Posicao da ancora no mundo, ja na superficie do terreno.
   *
   * Devolve Y = ALTURA_SOLO, e nao zero. Os motores apoiam os objetos da tarefa
   * a partir daqui — com zero, tudo nascia enterrado dentro dos blocos de chao.
   */
  posicaoDaAncora(nome) {
    const p = POSICOES[nome];
    return new THREE.Vector3(p[0], p[1] + ALTURA_SOLO, p[2]);
  }

  // -------------------------------------------------------------------------
  // Canteiros — o que a crianca planta fica plantado
  // -------------------------------------------------------------------------

  restaurarCanteiros(estado) {
    for (const canteiro of estado.canteiros) {
      this.desenharPlantas(canteiro, estado.dia);
    }
  }

  /**
   * Desenha as plantas de um canteiro. O tamanho depende de quantos dias se
   * passaram desde o plantio — a planta cresce entre uma sessao e outra.
   */
  desenharPlantas(canteiro, diaAtual) {
    const anterior = this.plantas.get(canteiro.id);
    if (anterior) this.raiz.remove(anterior);

    const grupo = new THREE.Group();
    const base = this.posicaoDaAncora('canteiro');

    // A planta cresce entre uma sessao e outra: tres estagios modelados.
    const diasCrescendo = Math.max(0, diaAtual - (canteiro.plantado_dia ?? diaAtual));
    const estagio = Math.min(3, 1 + Math.floor(diasCrescendo / 2));

    const porCultura = {
      milho: 'milho', feijao: 'brocolis', abobora: 'girassol',
      cenoura: 'cenoura', girassol: 'girassol', brocolis: 'brocolis',
    };
    const cultura = porCultura[canteiro.cultura] ?? 'milho';
    const modelo = `${cultura}_${estagio}`;

    const quantidade = canteiro.quantidade ?? 5;
    const posicoes = posicoesDeBuracos(base, quantidade, canteiro.agrupar_por ?? 5);

    for (let i = 0; i < quantidade; i++) {
      const planta = clonar(modelo);
      planta.position.set(posicoes[i].x, ALTURA_SOLO, posicoes[i].z);
      planta.rotation.y = (i * 1.7) % (Math.PI * 2);
      grupo.add(planta);
    }

    this.raiz.add(grupo);
    this.plantas.set(canteiro.id, grupo);
    return grupo;
  }

  /** Move os animais devagar em volta de onde moram. */
  atualizar(dt) {
    this.tempo = (this.tempo ?? 0) + dt;
    for (const bicho of this.animais) {
      const { casa, fase } = bicho.userData;
      const t = this.tempo * 0.35 + fase;
      bicho.position.x = casa.x + Math.cos(t) * 1.2;
      bicho.position.z = casa.z + Math.sin(t * 0.8) * 1.2;
      bicho.rotation.y = -t;
    }
  }

  /** Conta triangulos da cena, para o orcamento da secao 12. */
  conferirOrcamento() {
    let triangulos = 0;
    this.sm.cena.traverse((objeto) => {
      if (objeto.isMesh && objeto.geometry) {
        const indice = objeto.geometry.index;
        triangulos += indice
          ? indice.count / 3
          : objeto.geometry.attributes.position.count / 3;
      }
    });

    const LIMITE = 40_000;
    if (triangulos > LIMITE) {
      console.warn(
        `FAZENDA: a cena tem ${Math.round(triangulos)} triangulos, acima do limite de ` +
          `${LIMITE} da secao 12. Simplifique a geometria antes de testar no Chromebook.`
      );
    } else {
      console.info(`FAZENDA: ${Math.round(triangulos)} triangulos em cena (limite ${LIMITE}).`);
    }
    return triangulos;
  }
}
